// SPDX-License-Identifier: BUSL-1.1

/**
 * Audit log page.
 *
 */

import { type ColumnDef } from '@tanstack/react-table';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api-client';
import { AuditLogSkeleton } from '@/components/skeletons';
import { DataTable } from '@/components/data-table';
import { type AuditResponse, type AuditEntry, ACTOR_BADGE, ACTION_COLORS, actionSuffix } from '@/types/api';
import { relativeTime } from '@/lib/format';

const columns: ColumnDef<AuditEntry>[] = [
  {
    accessorKey: 'createdAt',
    header: 'Timestamp',
    cell: ({ row }) => (
      <span className="text-neutral-400" title={row.original.createdAt}>
        {relativeTime(row.original.createdAt)}
      </span>
    ),
  },
  {
    accessorKey: 'actorId',
    header: 'Actor',
    cell: ({ row }) => (
      <>
        <span className="text-neutral-200">{row.original.actorId}</span>
        <span className={`ml-2 rounded px-2 py-0.5 text-xs ${ACTOR_BADGE[row.original.actorType] ?? ACTOR_BADGE.system}`}>
          {row.original.actorType}
        </span>
      </>
    ),
  },
  {
    accessorKey: 'action',
    header: 'Action',
    cell: ({ row }) => (
      <span className={`font-mono ${ACTION_COLORS[actionSuffix(row.original.action)] ?? 'text-neutral-300'}`}>
        {row.original.action}
      </span>
    ),
  },
  {
    id: 'resource',
    header: 'Resource',
    enableSorting: false,
    cell: ({ row }) => (
      <span className="text-neutral-300">
        {row.original.resourceType}/{row.original.resourceId}
      </span>
    ),
  },
];

export function AuditPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['audit'],
    queryFn: () => apiFetch<AuditResponse>('/audit'),
  });

  return (
    <div>
      <h1 className="mb-6 text-2xl font-bold text-neutral-100">Audit Log</h1>

      {isLoading && <AuditLogSkeleton />}

      {data && <DataTable columns={columns} data={data.entries} emptyMessage="No audit entries yet." />}
    </div>
  );
}
